import type {
  JsonValue,
  OntologyReleaseManifest,
  OntologyStatement,
} from "./types";

export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(",")}}`;
}

export async function sha256Text(value: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return `sha256:${Array.from(new Uint8Array(digest))
    .map((item) => item.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function normalizeJsonValue<T extends JsonValue>(value: T): T {
  return JSON.parse(stableStringify(value)) as T;
}

export function normalizeStatement(
  statement: OntologyStatement,
): OntologyStatement {
  const objectValue = statement.objectValue ?? null;
  const objectJson = statement.objectJson
    ? normalizeJsonValue(statement.objectJson)
    : null;
  const hasValue = objectValue !== null;
  const hasJson = objectJson !== null;
  if (hasValue === hasJson) {
    throw new Error(
      `ONTOLOGY_STATEMENT_XOR: ${statement.subject} ${statement.predicate}`,
    );
  }
  for (const [field, value] of Object.entries({
    subject: statement.subject,
    predicate: statement.predicate,
    graph: statement.graph,
  })) {
    if (typeof value !== "string" || value.trim().length === 0) {
      throw new Error(`ONTOLOGY_STATEMENT_FIELD: ${field}`);
    }
  }
  return {
    subject: statement.subject,
    predicate: statement.predicate,
    objectValue,
    objectJson,
    graph: statement.graph,
  };
}

export function normalizeStatements(
  statements: OntologyStatement[],
): OntologyStatement[] {
  if (statements.length === 0) {
    throw new Error("ONTOLOGY_STATEMENTS_EMPTY");
  }
  const normalized = statements
    .map(normalizeStatement)
    .sort((left, right) =>
      stableStringify(left).localeCompare(stableStringify(right)),
    );
  for (let index = 1; index < normalized.length; index += 1) {
    if (
      stableStringify(normalized[index - 1]) ===
      stableStringify(normalized[index])
    ) {
      throw new Error(
        `ONTOLOGY_STATEMENT_DUPLICATE: ${normalized[index].subject} ${normalized[index].predicate}`,
      );
    }
  }
  return normalized;
}

export async function statementSetHash(
  statements: OntologyStatement[],
): Promise<string> {
  return sha256Text(stableStringify(normalizeStatements(statements)));
}

export async function ontologyReleaseManifestHash(
  manifest: OntologyReleaseManifest,
): Promise<string> {
  const {
    releaseId: _releaseId,
    manifestHash: _manifestHash,
    status: _status,
    createdAt: _createdAt,
    ...manifestCore
  } = manifest;
  return sha256Text(stableStringify(manifestCore));
}

export function conflictKey(statement: OntologyStatement): string {
  return [statement.graph, statement.subject, statement.predicate].join(
    "\u001f",
  );
}

export async function objectHash(
  statement: OntologyStatement,
): Promise<string> {
  return sha256Text(
    stableStringify({
      objectValue: statement.objectValue,
      objectJson: statement.objectJson,
    }),
  );
}
